import { DateTime } from "luxon";

/**
 * Format date and time with customizable options
 * @param {string} show_date - Date in YYYY-MM-DD format
 * @param {string} show_time - Time in HH:MM:SS format
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (show_date, show_time, options = {}) => {
  // Default options
  const defaultOptions = {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    separator: " • ",
    includeOrdinal: true,
    format: "full" 
  };

  const formatOptions = { ...defaultOptions, ...options };

  if (!show_date || !show_time || typeof show_date !== "string" || typeof show_time !== "string") {
    return "Invalid date or time provided.";
  }

  const dateString = `${show_date}T${show_time}`;
  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return "Invalid date or time provided.";
  }

  let localeOptions = {};
  
  switch (formatOptions.format) {
    case 'short':
      localeOptions = {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true
      };
      break;
    case 'dateOnly':
      localeOptions = {
        weekday: formatOptions.weekday,
        month: formatOptions.month,
        day: "numeric",
        year: formatOptions.year
      };
      break;
    case 'timeOnly':
      localeOptions = {
        hour: formatOptions.hour,
        minute: formatOptions.minute,
        hour12: formatOptions.hour12
      };
      break;
    case 'full':
    default:
      localeOptions = {
        weekday: formatOptions.weekday,
        month: formatOptions.month,
        day: "numeric",
        year: formatOptions.year,
        hour: formatOptions.hour,
        minute: formatOptions.minute,
        hour12: formatOptions.hour12
      };
  }

  let formattedDate = date.toLocaleDateString("en-US", localeOptions);

  if (formatOptions.includeOrdinal) {
    const day = date.getDate();
    const suffix =
      day % 10 === 1 && day !== 11 ? "st" :
      day % 10 === 2 && day !== 12 ? "nd" :
      day % 10 === 3 && day !== 13 ? "rd" : "th";

    formattedDate = formattedDate.replace(/\b\d+\b/, match => `${match}${suffix}`);
  }

  if (formatOptions.format === 'dateOnly') {
    return formattedDate;
  } else if (formatOptions.format === 'timeOnly') {
    return formattedDate;
  } else {
    if (formatOptions.format === 'full' || formatOptions.format === 'short') {
      const timeOptions = {
        hour: formatOptions.hour,
        minute: formatOptions.minute,
        hour12: true
      };
      
      const timeString = date.toLocaleTimeString('en-US', timeOptions);
      
      return formattedDate.replace(",", "").replace(" at ", " • ");
    }
    
    return formattedDate.replace(",", " ").replace(" at ", formatOptions.separator);
  }
};

export const formatLongDateTime = (show_date, show_time) => {
  return formatDateTime(show_date, show_time, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    separator: " • "
  });
};

export const formatShortDateTime = (show_date, show_time) => {
  return formatDateTime(show_date, show_time, {
    weekday: "short",
    month: "short",
    format: "short"
  });
};

export const formatDateOnly = (show_date) => {
  return formatDateTime(show_date, "00:00:00", {
    format: "dateOnly"
  });
};

export const formatTimeOnly = (show_time) => {
  return formatDateTime("2023-01-01", show_time, {
    format: "timeOnly"
  });
};

/**
 * Calculate and format time difference between a date and now
 * @param {string} at - Date string in format "YYYY-MM-DD HH:mm:ss"
 * @param {number} status - Ticket status code
 * @param {string|Object} timezone - Optional timezone string (e.g., "America/Denver") or timezone object with details. Defaults to "UTC"
 * @param {Object} timezoneObject - Optional full timezone object from API (with title, short_code, etc.)
 * @param {boolean} debug - If true, always appends DEBUG INFO (for testing)
 * @returns {string} Formatted time difference message
 */
export const getTimeDifference = (at, status, timezone = "UTC", timezoneObject = null, debug = false) => {
  if (!at || typeof at !== "string") return "Invalid date provided.";

  // Extract timezone string and details
  let timezoneStr = "UTC";
  let timezoneTitle = null;
  let timezoneShortCode = null;
  
  if (typeof timezone === 'string') {
    timezoneStr = timezone;
  } else if (timezone && typeof timezone === 'object' && timezone.timezone) {
    timezoneStr = timezone.timezone;
    timezoneTitle = timezone.title || null;
    timezoneShortCode = timezone.short_code || null;
  }
  
  // Use timezoneObject if provided (for backward compatibility)
  if (timezoneObject && typeof timezoneObject === 'object') {
    if (timezoneObject.timezone) timezoneStr = timezoneObject.timezone;
    if (timezoneObject.title) timezoneTitle = timezoneObject.title;
    if (timezoneObject.short_code) timezoneShortCode = timezoneObject.short_code;
  }

  try {
    // Parse the date string in the specified timezone
    // Format is typically "YYYY-MM-DD HH:mm:ss"
    const showAtInTZ = DateTime.fromFormat(at, "yyyy-MM-dd HH:mm:ss", {
      zone: timezoneStr
    });

    if (!showAtInTZ.isValid) {
        // Try to parse in the specified timezone using different approach
        try {
          const showAtInTZ2 = DateTime.fromSQL(at, { zone: timezoneStr });
          if (showAtInTZ2.isValid) {
            const nowInTZ = DateTime.now().setZone(timezoneStr);
            // Subtract 2 hours from show time to activate tickets 2 hours before show
            const showAtMinus2Hours = showAtInTZ2.minus({ hours: 2 });
            
            // Calculate difference from actual show time (for hiding timer logic)
            const diffFromShow = showAtInTZ2.diff(nowInTZ, ['hours', 'minutes']);
            let diffFromShowHours = Math.floor(diffFromShow.hours);
            
            // Calculate difference from activation time (for countdown display)
            const diffFromActivation = showAtMinus2Hours.diff(nowInTZ, ['hours', 'minutes']);
            let diffFromActivationHours = Math.floor(diffFromActivation.hours);
            let diffFromActivationMinutes = Math.floor(diffFromActivation.minutes);
            
            if (diffFromActivationHours < 0) {
              diffFromActivationHours = 0;
              diffFromActivationMinutes = 0;
            }
            if (diffFromShowHours < 0) {
              diffFromShowHours = 0;
            }
            
            let days = Math.floor(diffFromActivationHours / 24);
            let remainingHours = diffFromActivationHours % 24;

            switch (status) {
              case 5:
                // Format the expired date and time
                const expiredDateStr = showAtInTZ2.toFormat('MMMM d, yyyy');
                const expiredTimeStr = showAtInTZ2.toFormat('h:mm a');
                return `Expired on ${expiredDateStr} at ${expiredTimeStr}`;
              case 4:
                return "This ticket as been already been used.";
              case 2:
                return "Ticket has been Refunded.";
              case 3:
                return "Ticket has been Transferred.";
              case 1:
                // Hide timer if less than 2 hours before actual show time
                if (diffFromShowHours < 2) {
                  const currentTimeStr = nowInTZ.toFormat('yyyy-MM-dd HH:mm:ss ZZZZ');
                  const showTimeStr = showAtInTZ2.toFormat('yyyy-MM-dd HH:mm:ss ZZZZ');
                  const timezoneOffset = nowInTZ.offsetNameShort || nowInTZ.toFormat('ZZZZ');
                  const timezoneDetails = timezoneTitle || timezoneShortCode 
                    ? `\nTimezone Title: ${timezoneTitle || 'N/A'}\nTimezone Code: ${timezoneShortCode || 'N/A'}` 
                    : '';
                  return `Please show this screen at the door.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
[DEBUG INFO - Testing]
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Current Time: ${currentTimeStr}
Show Time: ${showTimeStr}
Timezone: ${timezoneStr}${timezoneDetails}
Timezone Offset: ${timezoneOffset}
Time until show: ${diffFromShowHours} hours
Time until activation: ${diffFromActivationHours} hours ${diffFromActivationMinutes} minutes
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`;
                }
                return days > 0
                  ? `Ticket will activate in ${days} Days ${remainingHours} Hours.`
                  : `Ticket will activate in ${diffFromActivationHours} hours ${diffFromActivationMinutes} minutes.`;
              default:
                return "Unknown ticket status.";
            }
        }
      } catch (e) {
        // Continue to fallback
      }
      
      // Final fallback - use timezone for display even if parsing fails
      const showAt = new Date(at);
      if (isNaN(showAt.getTime())) return "Invalid date provided.";
      
      // Subtract 2 hours from show time to activate tickets 2 hours before show
      const showAtMinus2Hours = new Date(showAt.getTime() - 2 * 60 * 60 * 1000);
      
      const now = new Date();
      // Calculate difference from actual show time (for hiding timer logic)
      const diffFromShowMs = showAt - now;
      let diffFromShowHours = Math.floor(diffFromShowMs / (1000 * 60 * 60));
      
      // Calculate difference from activation time (for countdown display)
      const diffFromActivationMs = showAtMinus2Hours - now;
      let diffFromActivationHours = Math.floor(diffFromActivationMs / (1000 * 60 * 60));
      let diffFromActivationMinutes = Math.floor((diffFromActivationMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (diffFromActivationHours < 0) {
        diffFromActivationHours = 0;
        diffFromActivationMinutes = 0;
      }
      if (diffFromShowHours < 0) {
        diffFromShowHours = 0;
      }
      
      let days = Math.floor(diffFromActivationHours / 24);
      let remainingHours = diffFromActivationHours % 24;

      switch (status) {
        case 5:
          // Format the expired date and time
          try {
            const showAtInTZ = DateTime.fromJSDate(showAt).setZone(timezoneStr);
            const expiredDateStr = showAtInTZ.toFormat('MMMM d, yyyy');
            const expiredTimeStr = showAtInTZ.toFormat('h:mm a');
            return `Expired on ${expiredDateStr} at ${expiredTimeStr}`;
          } catch (e) {
            // Fallback formatting
            const expiredDate = new Date(showAt);
            const expiredDateStr = expiredDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
            const expiredTimeStr = expiredDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
            return `Expired on ${expiredDateStr} at ${expiredTimeStr}`;
          }
        case 4:
          return "This ticket as been already been used.";
        case 2:
          return "Ticket has been Refunded.";
        case 3:
          return "Ticket has been Transferred.";
        case 1:
          // Hide timer if less than 2 hours before actual show time
          if (diffFromShowHours < 2) {
            // Try to format in the specified timezone
            try {
              const nowInTZ = DateTime.now().setZone(timezoneStr);
              const showAtInTZ = DateTime.fromJSDate(showAt).setZone(timezoneStr);
              const currentTimeStr = nowInTZ.toFormat('yyyy-MM-dd HH:mm:ss ZZZZ');
              const showTimeStr = showAtInTZ.toFormat('yyyy-MM-dd HH:mm:ss ZZZZ');
              const timezoneOffset = nowInTZ.offsetNameShort || nowInTZ.toFormat('ZZZZ');
              const timezoneDetails = timezoneTitle || timezoneShortCode 
                ? `\nTimezone Title: ${timezoneTitle || 'N/A'}\nTimezone Code: ${timezoneShortCode || 'N/A'}` 
                : '';
              return `Please show this screen at the door.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
[DEBUG INFO - Testing (Fallback)]
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Current Time: ${currentTimeStr}
Show Time: ${showTimeStr}
Timezone: ${timezoneStr}${timezoneDetails}
Timezone Offset: ${timezoneOffset}
Time until show: ${diffFromShowHours} hours
Time until activation: ${diffFromActivationHours} hours ${diffFromActivationMinutes} minutes
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`;
            } catch (e) {
              // If timezone formatting fails, show raw times
              const timezoneDetails = timezoneTitle || timezoneShortCode 
                ? `\nTimezone Title: ${timezoneTitle || 'N/A'}\nTimezone Code: ${timezoneShortCode || 'N/A'}` 
                : '';
              return `Please show this screen at the door.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
[DEBUG INFO - Testing (Parsing Failed)]
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Current Time: ${now.toISOString()}
Show Time: ${showAt.toISOString()}
Timezone: ${timezoneStr} (parsing failed)${timezoneDetails}
Time until show: ${diffFromShowHours} hours
Time until activation: ${diffFromActivationHours} hours ${diffFromActivationMinutes} minutes
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`;
            }
          }
          return days > 0
            ? `Ticket will activate in ${days} Days ${remainingHours} Hours.`
            : `Ticket will activate in ${diffFromActivationHours} hours ${diffFromActivationMinutes} minutes.`;
        default:
          return "Unknown ticket status.";
      }
    }

    // Get current time in the same timezone
    const nowInTZ = DateTime.now().setZone(timezoneStr);
    
    // Keep original show time for expired tickets display
    // Subtract 2 hours from show time to activate tickets 2 hours before show
    const showAtMinus2Hours = showAtInTZ.minus({ hours: 2 });
    
    // Calculate difference from actual show time (for hiding timer logic)
    const diffFromShow = showAtInTZ.diff(nowInTZ, ['hours', 'minutes']);
    let diffFromShowHours = Math.floor(diffFromShow.hours);
    let diffFromShowMinutes = Math.floor(diffFromShow.minutes);
    
    // Calculate difference from activation time (for countdown display)
    const diffFromActivation = showAtMinus2Hours.diff(nowInTZ, ['hours', 'minutes']);
    let diffFromActivationHours = Math.floor(diffFromActivation.hours);
    let diffFromActivationMinutes = Math.floor(diffFromActivation.minutes);
    
    // Handle negative differences (past dates)
    if (diffFromActivationHours < 0) {
      diffFromActivationHours = 0;
      diffFromActivationMinutes = 0;
    }
    if (diffFromShowHours < 0) {
      diffFromShowHours = 0;
      diffFromShowMinutes = 0;
    }

    let days = Math.floor(diffFromActivationHours / 24);
    let remainingHours = diffFromActivationHours % 24;

    const buildDebugBlock = () => {
      const currentTimeStr = nowInTZ.toFormat("yyyy-MM-dd HH:mm:ss ZZZZ");
      const showTimeStr = showAtInTZ.toFormat("yyyy-MM-dd HH:mm:ss ZZZZ");
      const timezoneOffset = nowInTZ.offsetNameShort || nowInTZ.toFormat("ZZZZ");
      const timezoneDetails =
        timezoneTitle || timezoneShortCode
          ? `\nTimezone Title: ${timezoneTitle || "N/A"}\nTimezone Code: ${timezoneShortCode || "N/A"}`
          : "";
      return `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
[DEBUG INFO - Testing]
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Current Time: ${currentTimeStr}
Show Time: ${showTimeStr}
Timezone: ${timezoneStr}${timezoneDetails}
Timezone Offset: ${timezoneOffset}
Time until show: ${diffFromShowHours} hours ${diffFromShowMinutes} minutes
Time until activation: ${diffFromActivationHours} hours ${diffFromActivationMinutes} minutes
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`;
    };

    const maybeAppendDebug = (message) => {
      if (!debug) return message;
      return `${message}\n\n${buildDebugBlock()}`;
    };

    switch (status) {
      case 5:
        // Format the expired date and time using original show time (not minus 2 hours)
        const expiredDateStr = showAtInTZ.toFormat('MMMM d, yyyy');
        const expiredTimeStr = showAtInTZ.toFormat('h:mm a');
        return maybeAppendDebug(`Expired on ${expiredDateStr} at ${expiredTimeStr}`);
      case 4:
        return maybeAppendDebug("This ticket as been already been used.");
      case 2:
        return maybeAppendDebug("Ticket has been Refunded.");
      case 3:
        return maybeAppendDebug("Ticket has been Transferred.");
      case 1: {
        // Hide timer if less than 2 hours before actual show time
        if (diffFromShowHours < 2) return maybeAppendDebug("Please show this screen at the door.");
        // Show countdown using activation time difference
        const msg =
          days > 0
            ? `Ticket will activate in ${days} Days ${remainingHours} Hours.`
            : `Ticket will activate in ${diffFromActivationHours} hours ${diffFromActivationMinutes} minutes.`;
        return maybeAppendDebug(msg);
      }
      default:
        return maybeAppendDebug("Unknown ticket status.");
    }
  } catch (error) {
    // Fallback calculation - still try to use timezone from API
    console.error("Error calculating time difference with timezone:", error);
    const showAt = new Date(at);
    if (isNaN(showAt.getTime())) return "Invalid date provided.";

    // Try to convert to timezone for display
    try {
      const nowInTZ = DateTime.now().setZone(timezoneStr);
      // For expired tickets, use original show time (not minus 2 hours)
      const showAtInTZOriginal = DateTime.fromJSDate(showAt).setZone(timezoneStr);
      // Subtract 2 hours from show time to activate tickets 2 hours before show
      const showAtInTZ = showAtInTZOriginal.minus({ hours: 2 });
      const now = new Date();
      
      // Calculate difference from actual show time (for hiding timer logic)
      const diffFromShow = showAtInTZOriginal.diff(nowInTZ, ['hours', 'minutes']);
      let diffFromShowHours = Math.floor(diffFromShow.hours);
      
      // Calculate difference from activation time (for countdown display)
      const diffFromActivation = showAtInTZ.diff(nowInTZ, ['hours', 'minutes']);
      let diffFromActivationHours = Math.floor(diffFromActivation.hours);
      let diffFromActivationMinutes = Math.floor(diffFromActivation.minutes);
      
      if (diffFromActivationHours < 0) {
        diffFromActivationHours = 0;
        diffFromActivationMinutes = 0;
      }
      if (diffFromShowHours < 0) {
        diffFromShowHours = 0;
      }
      
      let days = Math.floor(diffFromActivationHours / 24);
      let remainingHours = diffFromActivationHours % 24;

      switch (status) {
        case 5:
          // Format the expired date and time using original show time
          const expiredDateStr = showAtInTZOriginal.toFormat('MMMM d, yyyy');
          const expiredTimeStr = showAtInTZOriginal.toFormat('h:mm a');
          return `Expired on ${expiredDateStr} at ${expiredTimeStr}`;
        case 4:
          return "This ticket as been already been used.";
        case 2:
          return "Ticket has been Refunded.";
        case 3:
          return "Ticket has been Transferred.";
        case 1:
          // Hide timer if less than 2 hours before actual show time
          if (diffFromShowHours < 2) {
              const currentTimeStr = nowInTZ.toFormat('yyyy-MM-dd HH:mm:ss ZZZZ');
              const showTimeStr = showAtInTZOriginal.toFormat('yyyy-MM-dd HH:mm:ss ZZZZ');
              const timezoneOffset = nowInTZ.offsetNameShort || nowInTZ.toFormat('ZZZZ');
              const timezoneDetails = timezoneTitle || timezoneShortCode 
                ? `\nTimezone Title: ${timezoneTitle || 'N/A'}\nTimezone Code: ${timezoneShortCode || 'N/A'}` 
                : '';
              return `Please show this screen at the door.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
[DEBUG INFO - Testing (Error Fallback)]
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Current Time: ${currentTimeStr}
Show Time: ${showTimeStr}
Timezone: ${timezoneStr}${timezoneDetails}
Timezone Offset: ${timezoneOffset}
Time until show: ${diffFromShowHours} hours
Time until activation: ${diffFromActivationHours} hours ${diffFromActivationMinutes} minutes
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`;
          }
          return days > 0
            ? `Ticket will activate in ${days} Days ${remainingHours} Hours.`
            : `Ticket will activate in ${diffFromActivationHours} hours ${diffFromActivationMinutes} minutes.`;
        default:
          return "Unknown ticket status.";
      }
    } catch (e) {
      // Final fallback if timezone conversion fails
      const now = new Date();
      // Subtract 2 hours from show time to activate tickets 2 hours before show
      const showAtMinus2Hours = new Date(showAt.getTime() - 2 * 60 * 60 * 1000);
      
      // Calculate difference from actual show time (for hiding timer logic)
      const diffFromShowMs = showAt - now;
      let diffFromShowHours = Math.floor(diffFromShowMs / (1000 * 60 * 60));
      
      // Calculate difference from activation time (for countdown display)
      const diffFromActivationMs = showAtMinus2Hours - now;
      let diffFromActivationHours = Math.floor(diffFromActivationMs / (1000 * 60 * 60));
      let diffFromActivationMinutes = Math.floor((diffFromActivationMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (diffFromActivationHours < 0) {
        diffFromActivationHours = 0;
        diffFromActivationMinutes = 0;
      }
      if (diffFromShowHours < 0) {
        diffFromShowHours = 0;
      }
      
      let days = Math.floor(diffFromActivationHours / 24);
      let remainingHours = diffFromActivationHours % 24;

      switch (status) {
        case 5:
          // Format the expired date and time
          try {
            const showAtInTZ = DateTime.fromJSDate(showAt).setZone(timezoneStr);
            const expiredDateStr = showAtInTZ.toFormat('MMMM d, yyyy');
            const expiredTimeStr = showAtInTZ.toFormat('h:mm a');
            return `Expired on ${expiredDateStr} at ${expiredTimeStr}`;
          } catch (e) {
            // Fallback formatting
            const expiredDate = new Date(showAt);
            const expiredDateStr = expiredDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
            const expiredTimeStr = expiredDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
            return `Expired on ${expiredDateStr} at ${expiredTimeStr}`;
          }
        case 4:
          return "This ticket as been already been used.";
        case 2:
          return "Ticket has been Refunded.";
        case 3:
          return "Ticket has been Transferred.";
        case 1:
          // Hide timer if less than 2 hours before actual show time
          if (diffFromShowHours < 2) {
            const timezoneDetails = timezoneTitle || timezoneShortCode 
              ? `\nTimezone Title: ${timezoneTitle || 'N/A'}\nTimezone Code: ${timezoneShortCode || 'N/A'}` 
              : '';
            return `Please show this screen at the door.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
[DEBUG INFO - Testing (Final Fallback)]
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Current Time: ${now.toISOString()}
Show Time: ${showAt.toISOString()}
Timezone: ${timezoneStr} (conversion failed)${timezoneDetails}
Time until show: ${diffFromShowHours} hours
Time until activation: ${diffFromActivationHours} hours ${diffFromActivationMinutes} minutes
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`;
          }
          return days > 0
            ? `Ticket will activate in ${days} Days ${remainingHours} Hours.`
            : `Ticket will activate in ${diffFromActivationHours} hours ${diffFromActivationMinutes} minutes.`;
        default:
          return "Unknown ticket status.";
      }
    }
  }
};

/**
 * Extract timezone string from venue/show data
 * Handles both nested object format and string format
 * @param {Object} data - Show or venue data object
 * @returns {string} IANA timezone string or "UTC" as fallback
 */
export const getVenueTimezone = (data) => {
  if (!data) return "UTC";
  
  // Check if timezone is nested object (venue.timezone.timezone)
  if (data?.venue?.timezone?.timezone) {
    return data.venue.timezone.timezone;
  }
  
  // Check if timezone is a string (venue.timezone)
  if (typeof data?.venue?.timezone === 'string') {
    return data.venue.timezone;
  }
  
  // Check for time_zone (alternative field name)
  if (data?.venue?.time_zone) {
    return data.venue.time_zone;
  }
  
  // Check if timezone is in show object (show.timezone.timezone)
  if (data?.timezone?.timezone) {
    return data.timezone.timezone;
  }
  
  // Check if timezone is a string in show object
  if (typeof data?.timezone === 'string') {
    return data.timezone;
  }
  
  // Fallback to UTC
  return "UTC";
};

/**
 * Get the current date in ISO format
 * @returns {string} Current date in ISO format
 */
export const getCurrentDateISO = () => new Date().toISOString();

export const formatMonthDayYear = (show_date) => {
  return formatDateTime(show_date, "00:00:00", {
    weekday: undefined,
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: undefined,
    minute: undefined,
    hour12: false,
    format: "dateOnly",
    includeOrdinal: true
  });
}; 